import React, { useState } from 'react';
import { Container, Header, SpaceBetween, Box, Spinner } from '@cloudscape-design/components';
import { useTranslation } from 'react-i18next';
import { ChatInput } from './ChatInput';
import { ValueWithLabel } from './ValueWithLabel';
import * as api from '../api/api';

/**
 * Panel de consultas a la IA reutilizable.
 * Soporta dos contextos:
 * - type: 'call', filename: key → consulta sobre una transcripción (genaiquery)
 * - type: 'summary', historyId: id → consulta sobre el conjunto de llamadas del resumen (summary/query)
 *
 * @param {Object} queryContext - { type, filename? | historyId? }
 * @param {string} [id] - id opcional para el div del chat (scroll)
 */
export function GenAIQueryPanel({ queryContext, id = 'chatDiv' }) {
  const { t } = useTranslation();
  const [genAiQueries, setGenAiQueries] = useState([]);
  const [genAiQueryStatus, setGenAiQueryStatus] = useState(false);

  const isValidContext =
    queryContext &&
    ((queryContext.type === 'call' && queryContext.filename) ||
      (queryContext.type === 'summary' && queryContext.historyId));

  const getElementByIdAsync = (elementId) =>
    new Promise((resolve) => {
      const check = () => {
        const el = document.getElementById(elementId);
        if (el) resolve(el);
        else requestAnimationFrame(check);
      };
      check();
    });

  const scrollToBottom = async () => {
    try {
      const chatDiv = await getElementByIdAsync(id);
      if (chatDiv) chatDiv.scrollTop = chatDiv.scrollHeight + 200;
    } catch {
      /* ignore */
    }
  };

  const submitQuery = async (query) => {
    if (genAiQueryStatus || !isValidContext) return;

    setGenAiQueryStatus(true);
    const responseData = { label: query, value: '...' };
    const currentQueries = genAiQueries.concat(responseData);
    setGenAiQueries(currentQueries);
    scrollToBottom();

    try {
      let data;
      if (queryContext.type === 'call') {
        data = await api.genaiquery(queryContext.filename, query);
      } else {
        data = await api.querySummaryCalls(queryContext.historyId, query);
      }

      const updated = currentQueries.map((q) =>
        q.value === '...' ? { ...q, value: data?.response ?? '' } : q
      );
      setGenAiQueries(updated);
      scrollToBottom();
    } catch (err) {
      console.error('Error en consulta GenAI:', err);
      const updated = currentQueries.map((q) =>
        q.value === '...'
          ? { ...q, value: t('genaiQuery.error', { defaultValue: 'Error al procesar la consulta.' }) }
          : q
      );
      setGenAiQueries(updated);
    } finally {
      setGenAiQueryStatus(false);
    }
  };

  if (!isValidContext) {
    return (
      <Container
        header={<Header variant="h2">{t('genaiQuery')}</Header>}
      >
        <Box color="text-body-secondary" padding="l">
          {t('insights.detail.aiQueries.selectContext', {
            defaultValue: 'Selecciona un contexto para consultar.',
          })}
        </Box>
      </Container>
    );
  }

  return (
    <Container
      fitHeight={false}
      style={{ maxWidth: '100%!important' }}
      header={<Header variant="h2">{t('genaiQuery')}</Header>}
      footer={<ChatInput submitQuery={submitQuery} />}
    >
      <div id={id} style={{ overflow: 'hidden', overflowY: 'auto', height: '30em' }}>
        <SpaceBetween size="m">
          {genAiQueries.length > 0 ? (
            genAiQueries.map((entry, i) => (
              <ValueWithLabel key={i} index={i} label={entry.label}>
                {entry.value === '...' ? (
                  <div style={{ height: '30px' }}>
                    <Spinner />
                  </div>
                ) : (
                  entry.value
                )}
              </ValueWithLabel>
            ))
          ) : (
            <ValueWithLabel key="nosummary">{t('question')}</ValueWithLabel>
          )}
        </SpaceBetween>
      </div>
    </Container>
  );
}
